import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Trash2, RefreshCw } from "lucide-react";
import Shell from "@/components/Shell";
import { Card, PageHeader, Btn, Spinner, Empty, StatusBadge, IconBtn } from "@/components/ui-kit";
import { formatGBP } from "@/lib/format";
import api from "@/lib/api";
import { useConfirm } from "@/lib/confirm";

const xeroEditUrl = (invoiceId) =>
  invoiceId ? `https://go.xero.com/app/invoicing/edit/${invoiceId}` : null;

const formatDate = (raw) => {
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const XERO_STATUS_STYLES = {
  PAID: "bg-emerald-50 text-emerald-700 border-emerald-200",
  AUTHORISED: "bg-amber-50 text-amber-800 border-amber-200",
  SUBMITTED: "bg-amber-50 text-amber-800 border-amber-200",
  DRAFT: "bg-zinc-100 text-zinc-700 border-zinc-200",
  VOIDED: "bg-red-50 text-red-700 border-red-200",
  DELETED: "bg-red-50 text-red-700 border-red-200",
};

const XeroStatusPill = ({ status, amountDue, amountPaid, total }) => {
  if (!status) return null;
  const cls = XERO_STATUS_STYLES[status] || "bg-zinc-100 text-zinc-700 border-zinc-200";
  const label =
    status === "PAID"
      ? `Paid · ${formatGBP(amountPaid || total || 0)}`
      : status === "AUTHORISED" && amountDue && amountPaid
      ? `Part-paid · ${formatGBP(amountDue)} due`
      : status === "AUTHORISED"
      ? `Awaiting payment · ${formatGBP(amountDue || total || 0)} due`
      : status.charAt(0) + status.slice(1).toLowerCase();
  return (
    <span
      data-testid="xero-status-pill"
      className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${cls}`}
    >
      {label}
    </span>
  );
};

const Invoiced = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const confirm = useConfirm();

  const load = () => {
    setLoading(true);
    return api
      .get("/projects")
      .then((r) => {
        const invoiced = r.data.filter((p) => p.status === "Invoiced");
        // Most recent first — prefer xero_pushed_at, fall back to end_date / start_date
        const ts = (p) => {
          const v = p.xero_pushed_at || p.end_date || p.start_date;
          if (!v) return 0;
          const d = new Date(v);
          return isNaN(d.getTime()) ? 0 : d.getTime();
        };
        invoiced.sort((a, b) => ts(b) - ts(a));
        setItems(invoiced);
        return invoiced;
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    // 1. Show what we have immediately (fast).
    // 2. Then quietly refresh Xero statuses in the background so paid invoices
    //    flip to "Paid" without the user having to hit the manual button.
    load().then((invoiced) => {
      if (invoiced && invoiced.some((p) => p.xero_invoice_id)) {
        setRefreshing(true);
        api
          .post("/projects/refresh-xero-statuses")
          .then(() => {
            setRefreshedAt(new Date());
            return load();
          })
          .catch(() => {
            // Silent failure on auto-refresh — user can hit manual button if needed.
          })
          .finally(() => setRefreshing(false));
      }
    });
  }, []);

  const refreshStatuses = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/projects/refresh-xero-statuses");
      setRefreshedAt(new Date());
      if (data.cleared > 0) {
        // Some invoices were deleted/voided in Xero — re-load to drop them
      }
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to refresh";
      alert(`Could not refresh Xero statuses:\n\n${msg}`);
    } finally {
      setRefreshing(false);
    }
  };

  const deleteInvoice = async (p) => {
    if (
      !(await confirm({
        title: "Delete this invoice?",
        description: `The draft invoice ${p.xero_invoice_number || ""} will be voided in Xero (if still there) and "${p.name}" will be moved back to Completed status.`,
        confirmText: "Delete invoice",
        destructive: true,
      }))
    )
      return;
    setBusy((s) => ({ ...s, [p.id]: true }));
    try {
      await api.delete(`/projects/${p.id}/xero-invoice`);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to delete invoice";
      alert(`Could not delete invoice:\n\n${msg}`);
    } finally {
      setBusy((s) => ({ ...s, [p.id]: false }));
    }
  };

  const hasAnyXero = items.some((p) => p.xero_invoice_id);

  return (
    <Shell>
      <PageHeader
        testId="invoiced-header"
        title="Invoiced"
        subtitle="Projects that have been invoiced"
        actions={
          hasAnyXero ? (
            <Btn
              size="sm"
              variant="secondary"
              onClick={refreshStatuses}
              disabled={refreshing}
              data-testid="refresh-xero-status-btn"
              title="Pull live invoice status from Xero"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing…" : "Refresh Xero status"}
            </Btn>
          ) : null
        }
      />
      {refreshedAt && (
        <p className="text-[11px] text-zinc-400 mb-3" data-testid="xero-refreshed-at">
          Last refreshed {refreshedAt.toLocaleTimeString()}
        </p>
      )}
      {loading ? (
        <div className="flex justify-center py-20">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <Empty
          title="Nothing invoiced yet"
          description="Mark projects as 'Invoiced' from the project detail page — they'll show up here."
        />
      ) : (
        <Card className="p-2">
          <div className="divide-y divide-zinc-100">
            {items.map((p) => (
              <div
                key={p.id}
                data-testid={`invoiced-item-${p.id}`}
                className="flex items-center justify-between gap-3 py-3 px-4 hover:bg-zinc-50/60 rounded-lg transition-colors"
              >
                <Link to={`/projects/${p.id}`} className="flex-1 min-w-0 group">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-zinc-950 group-hover:text-zinc-700 transition-colors truncate">
                      {p.name}
                    </p>
                    <XeroStatusPill
                      status={p.xero_invoice_status}
                      amountDue={p.xero_amount_due}
                      amountPaid={p.xero_amount_paid}
                      total={p.xero_invoice_total}
                    />
                  </div>
                  <p className="text-xs text-zinc-500 mt-0.5 truncate">
                    {p.client_name} · Job #{p.job_number}
                    {p.xero_invoice_number ? (
                      <span className="ml-2 text-emerald-700">· {p.xero_invoice_number}</span>
                    ) : null}
                  </p>
                </Link>
                <div className="flex items-center gap-3 shrink-0">
                  <span
                    className="text-xs text-zinc-500 tabular-nums hidden md:inline"
                    data-testid={`invoiced-date-${p.id}`}
                    title={p.xero_pushed_at ? `Pushed to Xero` : `Project end date`}
                  >
                    {formatDate(p.xero_pushed_at || p.end_date || p.start_date)}
                  </span>
                  <span className="text-sm font-semibold text-zinc-950">
                    {formatGBP(p.total_cost)}
                  </span>
                  <StatusBadge status={p.status} />
                  {p.xero_invoice_id && (
                    <a
                      href={xeroEditUrl(p.xero_invoice_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`invoiced-view-xero-${p.id}`}
                      title="Open invoice in Xero"
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 transition-colors"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      View in Xero
                    </a>
                  )}
                  <IconBtn
                    variant="destructive"
                    onClick={() => deleteInvoice(p)}
                    disabled={busy[p.id]}
                    title="Delete invoice (voids in Xero)"
                    data-testid={`invoiced-delete-${p.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </IconBtn>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </Shell>
  );
};

export default Invoiced;
